import styled from "styled-components";
import {
  Checkbox,
  Dropdown,
  IDropdownOption,
  TextField,
} from "@fluentui/react";
import React, { useContext } from "react";
import { useNumberFormat } from "../use-number-format/useNumberFormat";
import { ColumnOptionsContext } from "./ColumnOptions";
import {
  Column,
  NumberFilter as NumberFilterType,
  NumberFilterOperator,
  numberFilterOperators,
} from "./types";

const capitalize = (s: string) =>
  s.length > 0 ? `${s[0].toUpperCase()}${s.slice(1)}` : "";

type Props = {
  column: Column;
};

export const NumberFilter = ({ column }: Props) => {
  const filter = column.filter as NumberFilterType;
  const { setFilter, strings } = useContext(ColumnOptionsContext);

  const operatorOptions: IDropdownOption[] = numberFilterOperators.map((o) => ({
    key: o,
    text: (strings as any)[`numberFilterOperator${capitalize(o)}Label`],
  }));

  const showCheckboxes =
    filter.operator !== "eq" || filter.operator === ("between" as any);

  return (
    <Container>
      <Dropdown
        selectedKey={filter.operator}
        options={operatorOptions}
        onChange={(_, option) => {
          if (!option) return;
          setFilter(column.key, {
            ...filter,
            operator: option.key as NumberFilterOperator,
          });
        }}
      />
      {showCheckboxes && (
        <CheckboxRow>
          {filter.operator !== "eq" && (
            <Checkbox
              label={strings.numberFilterIncludeLabel}
              checked={filter.includeNumber1}
              onChange={(_, checked) => {
                setFilter(column.key, {
                  ...filter,
                  includeNumber1: checked === true,
                });
              }}
            />
          )}
          {filter.operator === "between" && (
            <ReverseCheckbox
              label={strings.numberFilterIncludeLabel}
              checked={filter.includeNumber2}
              onChange={(_, checked) => {
                setFilter(column.key, {
                  ...filter,
                  includeNumber2: checked === true,
                });
              }}
            />
          )}
        </CheckboxRow>
      )}
      <NumberFieldsGrid>
        <NumberField
          value={filter.number1 !== null ? filter.number1.toString() : ""}
          onChange={(value) => {
            setFilter(column.key, {
              ...filter,
              number1: (value === "" ? null : value) as any,
            });
          }}
          isNegativeAllowed={filter.isNegativeAllowed}
          includeThousandSeparators={filter.includeThousandSeparators}
          format={filter.format}
          max={filter.max}
        />
        {filter.operator === "between" && (
          <NumberField
            value={filter.number2 !== null ? filter.number2.toString() : ""}
            onChange={(value) => {
              setFilter(column.key, {
                ...filter,
                number2: (value === "" ? null : value) as any,
              });
            }}
            isNegativeAllowed={filter.isNegativeAllowed}
            includeThousandSeparators={filter.includeThousandSeparators}
            format={filter.format}
            max={filter.max}
          />
        )}
      </NumberFieldsGrid>
      <FormulaText>
        {filter.operator === "gt" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          `${filter.includeNumber1 ? ">=" : ">"} ${filter.number1}`}
        {filter.operator === "lt" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          `${filter.includeNumber1 ? "<=" : "<"} ${filter.number1}`}
        {filter.operator === "eq" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          `= ${filter.number1}`}
        {filter.operator === "between" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          filter.number2 !== null &&
          filter.number2 !== undefined &&
          `${filter.includeNumber1 ? ">=" : ">"} ${filter.number1} ${
            strings.numberFilterAndLabel
          } ${filter.includeNumber2 ? "<=" : "<"} ${filter.number2}`}
      </FormulaText>
    </Container>
  );
};

const NumberField = ({
  value,
  onChange,
  max,
  placeholder,
  isNegativeAllowed = false,
  includeThousandSeparators,
  format,
}: {
  value: string;
  onChange: (value: string) => void;
  max?: number;
  placeholder?: string;
  isNegativeAllowed?: boolean;
  includeThousandSeparators?: boolean;
  format?: "integer" | "float";
}) => {
  const { value: _value, onChange: _onChange } = useNumberFormat({
    value,
    setValue: (value: string) => onChange(value),
    isNegativeAllowed,
    max,
    includeThousandSeparators,
    format,
  });
  return (
    <TextField
      value={_value}
      onChange={(_, newValue) => {
        _onChange({ target: { value: newValue ?? "" } } as any);
      }}
      placeholder={placeholder}
    />
  );
};

const Container = styled.div`
  height: 180px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
`;

const ReverseCheckbox = styled(Checkbox)`
  margin-left: auto;
  .ms-Checkbox-label {
    flex-direction: row-reverse;
  }
  .ms-Checkbox-text {
    margin: 0;
    margin-right: 4px;
  }
  .ms-Checkbox-checkbox {
    margin: 0;
    margin-left: 4px;
  }
`;

const NumberFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
  grid-gap: 5px;
`;

const FormulaText = styled.div`
  color: ${(p) => p.theme.palette.neutralPrimary};
  font-style: italic;
  margin-top: auto;
  font-weight: bold;
`;
